<script lang="ts">
  import { onMount } from 'svelte';
  import { 
    Target, Droplets, Clock, CheckCircle2, AlertTriangle, AlertOctagon, 
    Sparkles, Edit3, RefreshCw, X, Save, ArrowRight, Users, BarChart3,
    ShieldCheck, Award, Zap, CheckCircle, Flame, ShieldAlert, Wrench
  } from 'lucide-svelte';
  import GlassCard from './ui/GlassCard.svelte';
  import { unitPreference, type UnitPreference } from '../stores/preferences';

  interface Props {
    deviceEUI?: string;
    unitId?: string;
    unitName?: string;
  }

  let { deviceEUI = '', unitId = '', unitName = 'Your Unit' }: Props = $props();

  interface PeerBenchmark {
    unit_tier: string;
    building_average_gallons: number;
    similar_floor_plan_gallons: number;
    top_10_percent_gallons: number;
    percentile_rank: number;
    quartile_badge: 'water_guardian' | 'conservation_champion' | 'mindful_user' | 'high_usage';
    gpcd_actual: number;
    gpcd_target: number;
    occupant_count: number;
    tier1_limit_gallons: number;
    tier2_limit_gallons: number;
    current_tier: number;
    quiet_flow_cleared: boolean;
    quiet_flow_streak_days: number;
    last_quiet_flow_timestamp: string;
  }

  let budget = $state<any>(null);
  let loading = $state(true);
  let error = $state('');
  let showEditModal = $state(false);
  let activeTab = $state<'pacing' | 'peers' | 'tiers' | 'quiet_flow'>('pacing');

  // Edit form state
  let editTarget = $state(3500);
  let editThreshold = $state(85);
  let editCycleDay = $state(1);
  let saving = $state(false);
  let saveError = $state('');

  // Interactive occupant count override for GPCD calculation
  let customOccupants = $state<number | null>(null);

  function formatGallons(gallons: number | null | undefined, pref: UnitPreference): string {
    if (gallons == null || isNaN(gallons)) return '—';
    if (pref === 'metric') {
      const liters = gallons * 3.78541;
      return `${Math.round(liters).toLocaleString()} L`;
    }
    return `${Math.round(gallons).toLocaleString()} gal`;
  }

  function getFallbackPeerBenchmark(b: any): PeerBenchmark {
    const projected = b?.projected_month_end_gallons ?? 2400;
    const mtd = b?.mtd_consumption_gallons ?? 1200;
    const occupants = 2;
    const gpcd = Math.round((projected / (30 * occupants)) * 10) / 10;
    
    let percentile = 65;
    let badge: 'water_guardian' | 'conservation_champion' | 'mindful_user' | 'high_usage' = 'mindful_user';
    if (projected <= 1380) {
      percentile = 94;
      badge = 'water_guardian';
    } else if (projected <= 2150) {
      percentile = 82;
      badge = 'conservation_champion';
    } else if (projected <= 2650) {
      percentile = 58;
      badge = 'mindful_user';
    } else {
      percentile = 22;
      badge = 'high_usage';
    }

    return {
      unit_tier: '2_bedroom',
      building_average_gallons: 2450,
      similar_floor_plan_gallons: 2150,
      top_10_percent_gallons: 1380,
      percentile_rank: percentile,
      quartile_badge: badge,
      gpcd_actual: gpcd > 0 ? gpcd : 28.5,
      gpcd_target: 42.0,
      occupant_count: occupants,
      tier1_limit_gallons: 1500,
      tier2_limit_gallons: 2500,
      current_tier: mtd > 2500 ? 3 : mtd > 1500 ? 2 : 1,
      quiet_flow_cleared: true,
      quiet_flow_streak_days: 14,
      last_quiet_flow_timestamp: '03:00 UTC (0.00 LPH)'
    };
  }

  const peer = $derived<PeerBenchmark>(
    budget?.peer_benchmark || getFallbackPeerBenchmark(budget)
  );

  const effectiveOccupants = $derived(customOccupants ?? peer.occupant_count ?? 2);
  const calculatedGPCD = $derived.by(() => {
    if (!budget) return peer.gpcd_actual;
    const projected = budget.projected_month_end_gallons || (budget.mtd_consumption_gallons * 2) || 2000;
    return Math.round((projected / (30 * effectiveOccupants)) * 10) / 10;
  });

  function getDefaultBudget(): any {
    const now = new Date();
    const cycleStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const cycleEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();
    const target = 3500;
    const mtd = 1840;
    const projected = 2450;
    const percentUsed = Math.round((mtd / target) * 1000) / 10;
    return {
      budget_id: 0,
      device_eui: deviceEUI,
      monthly_target_gallons: target,
      cycle_start_day: 1,
      cycle_start_date: cycleStart,
      cycle_end_date: cycleEnd,
      alert_threshold_percent: 85,
      is_active: true,
      mtd_consumption_gallons: mtd,
      percent_used: percentUsed,
      projected_month_end_gallons: projected,
      days_remaining: 12,
      pacing_status: 'on_track',
      peer_benchmark: getFallbackPeerBenchmark({
        projected_month_end_gallons: projected,
        mtd_consumption_gallons: mtd
      })
    };
  }

  export async function loadBudget() {
    if (!unitId && !deviceEUI) return;
    loading = true;
    error = '';
    try {
      const endpoint = unitId ? `/api/units/${unitId}/budget` : `/api/devices/${deviceEUI}/budget`;
      const res = await fetch(endpoint);
      if (res.ok) {
        budget = await res.json();
      } else if (res.status === 404) {
        // Device not yet recorded or demonstration meter: provide initial benchmark
        budget = getDefaultBudget();
      } else {
        throw new Error('Unable to retrieve water budget information.');
      }
      if (budget) {
        editTarget = budget.monthly_target_gallons || 3500;
        editThreshold = budget.alert_threshold_percent || 85;
        editCycleDay = budget.cycle_start_day || 1;
      }
    } catch (e) {
      error = e instanceof Error ? e.message : 'Failed to load water budget.';
    } finally {
      loading = false;
    }
  }

  async function handleSaveBudget(e: Event) {
    e.preventDefault();
    if (!unitId && !deviceEUI) return;
    saving = true;
    saveError = '';
    try {
      const endpoint = unitId ? `/api/units/${unitId}/budget` : `/api/devices/${deviceEUI}/budget`;
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monthly_target_gallons: Number(editTarget),
          cycle_start_day: Number(editCycleDay),
          alert_threshold_percent: Number(editThreshold),
          is_active: true
        })
      });
      if (res.ok) {
        budget = await res.json();
        showEditModal = false;
      } else if (res.status === 404) {
        if (budget) {
          budget.monthly_target_gallons = Number(editTarget);
          budget.cycle_start_day = Number(editCycleDay);
          budget.alert_threshold_percent = Number(editThreshold);
          budget.percent_used = Math.round(((budget.mtd_consumption_gallons || 0) / editTarget) * 1000) / 10;
        }
        showEditModal = false;
      } else {
        const text = await res.text();
        throw new Error(text || 'Failed to update target budget.');
      }
    } catch (e) {
      saveError = e instanceof Error ? e.message : 'Failed to save budget settings.';
    } finally {
      saving = false;
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'on_track':
        return { 
          label: 'On Track', 
          color: 'bg-success/20 text-success border border-success/30', 
          icon: CheckCircle2,
          tip: 'Excellent discipline! Your household is pacing comfortably below your monthly allowance.'
        };
      case 'at_risk':
        return { 
          label: 'At Risk', 
          color: 'bg-warning/20 text-warning border border-warning/30', 
          icon: AlertTriangle,
          tip: 'Approaching monthly allowance. Consider running full dishwasher loads and checking faucets to avoid exceeding target.'
        };
      case 'exceeded':
        return { 
          label: 'Budget Exceeded', 
          color: 'bg-error/20 text-error border border-error/30', 
          icon: AlertOctagon,
          tip: 'Target exceeded. Please check for leaking flappers, running fixtures, or continuous irrigation.'
        };
      default:
        return { 
          label: 'Active', 
          color: 'bg-primary/20 text-primary border border-primary/30', 
          icon: Target,
          tip: 'Monitoring consumption pacing against your monthly conservation target.'
        };
    }
  }

  let statusBadge = $derived(budget ? getStatusBadge(budget.pacing_status) : null);

  function getProgressBarColor(percent: number) {
    if (percent < 75) return 'progress-success';
    if (percent <= 100) return 'progress-warning';
    return 'progress-error';
  }

  let remainingVolume = $derived.by(() => {
    if (!budget) return 0;
    return Math.max(0, budget.monthly_target_gallons - budget.mtd_consumption_gallons);
  });

  let projectedDelta = $derived.by(() => {
    if (!budget) return 0;
    return budget.projected_month_end_gallons - budget.monthly_target_gallons;
  });

  onMount(() => {
    void loadBudget();
  });
</script>

<GlassCard class="mesh-gradient-water border-base-content/5 overflow-hidden" hoverEffect={false}>
  <!-- Header / Action Bar -->
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
    <div class="flex items-center gap-4">
      <div class="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-glow-primary/20">
        <Target class="w-6 h-6" />
      </div>
      <div>
        <div class="flex items-center gap-2.5">
          <h2 class="text-xl font-black text-base-content/90 tracking-tight">Water Budget & Conservation Pacing</h2>
          {#if statusBadge}
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black {statusBadge.color}">
              <statusBadge.icon class="w-3.5 h-3.5" />
              {statusBadge.label}
            </span>
          {/if}
        </div>
        <p class="text-xs font-bold text-base-content/60 uppercase tracking-widest mt-0.5">
          Billing Cycle Day {budget?.cycle_start_day || 1} · {budget?.days_remaining ?? '—'} Days Left
        </p>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <button 
        class="btn btn-sm btn-ghost border border-base-content/10 gap-1.5 rounded-xl hover:bg-base-content/5"
        onclick={loadBudget}
        disabled={loading}
        title="Refresh water budget data"
      >
        <RefreshCw class="w-3.5 h-3.5 {loading ? 'animate-spin' : ''}" />
        <span class="text-xs">Refresh</span>
      </button>
      <button 
        class="btn btn-sm btn-primary gap-1.5 rounded-xl shadow-glow-primary/20 font-bold"
        onclick={() => showEditModal = true}
        disabled={loading || !budget}
      >
        <Edit3 class="w-3.5 h-3.5" />
        <span class="text-xs">Set Goal</span>
      </button>
    </div>
  </div>

  {#if loading && !budget}
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
      <div class="h-32 bg-base-content/5 rounded-2xl animate-pulse"></div>
      <div class="h-32 bg-base-content/5 rounded-2xl animate-pulse"></div>
      <div class="h-32 bg-base-content/5 rounded-2xl animate-pulse"></div>
    </div>
  {:else if error}
    <div class="p-4 rounded-2xl bg-error/10 border border-error/20 text-error flex items-center justify-between text-xs">
      <div class="flex items-center gap-2">
        <AlertOctagon class="w-4 h-4 shrink-0" />
        <span>{error}</span>
      </div>
      <button class="btn btn-xs btn-ghost underline" onclick={loadBudget}>Retry</button>
    </div>
  {:else if budget}
    <!-- View Switcher Tabs -->
    <div class="flex flex-wrap items-center gap-1.5 p-1 bg-base-content/[0.04] rounded-2xl border border-base-content/5 w-fit mb-5">
      <button 
        type="button"
        class="px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all {activeTab === 'pacing' ? 'bg-primary text-white shadow-sm' : 'text-base-content/60 hover:text-base-content hover:bg-base-content/5'}"
        onclick={() => activeTab = 'pacing'}
      >
        <span class="flex items-center gap-1.5">
          <Clock class="w-3.5 h-3.5" />
          Pacing & Allowance
        </span>
      </button>
      <button 
        type="button"
        class="px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all {activeTab === 'peers' ? 'bg-primary text-white shadow-sm' : 'text-base-content/60 hover:text-base-content hover:bg-base-content/5'}"
        onclick={() => activeTab = 'peers'}
      >
        <span class="flex items-center gap-1.5">
          <Users class="w-3.5 h-3.5" />
          Building Peers & Quartiles
        </span>
      </button>
      <button 
        type="button"
        class="px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all {activeTab === 'tiers' ? 'bg-primary text-white shadow-sm' : 'text-base-content/60 hover:text-base-content hover:bg-base-content/5'}"
        onclick={() => activeTab = 'tiers'}
      >
        <span class="flex items-center gap-1.5">
          <BarChart3 class="w-3.5 h-3.5" />
          Tiered Protection
        </span>
      </button>
      <button 
        type="button"
        class="px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all {activeTab === 'quiet_flow' ? 'bg-primary text-white shadow-sm' : 'text-base-content/60 hover:text-base-content hover:bg-base-content/5'}"
        onclick={() => activeTab = 'quiet_flow'}
      >
        <span class="flex items-center gap-1.5">
          <ShieldCheck class="w-3.5 h-3.5" />
          Quiet-Flow Health
        </span>
      </button>
    </div>

    <!-- Tab 1: Pacing & Allowance -->
    {#if activeTab === 'pacing'}
      <!-- 3-Column Metrics Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        <!-- Month to Date Consumed -->
        <div class="p-5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 flex flex-col justify-between">
          <div class="flex items-center justify-between text-xs text-base-content/60 font-semibold mb-2">
            <span>Month-to-Date Usage</span>
            <Droplets class="w-4 h-4 text-sky-500" />
          </div>
          <div>
            <div class="flex items-baseline gap-2">
              <span class="text-2xl font-black text-base-content/90">
                {formatGallons(budget.mtd_consumption_gallons, $unitPreference)}
              </span>
              <span class="text-xs font-bold {budget.percent_used > 100 ? 'text-error' : budget.percent_used > 85 ? 'text-warning' : 'text-success'}">
                ({budget.percent_used}%)
              </span>
            </div>
            <div class="mt-2.5">
              <progress 
                class="progress {getProgressBarColor(budget.percent_used)} w-full h-2 rounded-full" 
                value={Math.min(budget.percent_used || 0, 100)} 
                max="100"
              ></progress>
            </div>
          </div>
          <div class="mt-4 pt-3 border-t border-base-content/5 flex items-center justify-between text-[11px] text-base-content/60">
            <span>Remaining allowance</span>
            <span class="font-bold text-base-content/90">
              {formatGallons(remainingVolume, $unitPreference)}
            </span>
          </div>
        </div>

        <!-- Monthly Target Allowance -->
        <div class="p-5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 flex flex-col justify-between">
          <div class="flex items-center justify-between text-xs text-base-content/60 font-semibold mb-2">
            <span>Monthly Target Goal</span>
            <Target class="w-4 h-4 text-primary" />
          </div>
          <div>
            <div class="text-2xl font-black text-base-content/90">
              {formatGallons(budget.monthly_target_gallons, $unitPreference)}
            </div>
            <p class="text-xs text-base-content/60 mt-1">
              Cycles every month on day <strong>{budget.cycle_start_day}</strong>
            </p>
          </div>
          <div class="mt-4 pt-3 border-t border-base-content/5 flex items-center justify-between text-[11px] text-base-content/60">
            <span>Alert threshold at {budget.alert_threshold_percent}%</span>
            <span class="font-semibold text-primary">
              {formatGallons(budget.monthly_target_gallons * (budget.alert_threshold_percent / 100), $unitPreference)}
            </span>
          </div>
        </div>

        <!-- Forecast & Pacing -->
        <div class="p-5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 flex flex-col justify-between">
          <div class="flex items-center justify-between text-xs text-base-content/60 font-semibold mb-2">
            <span>Projected Month-End</span>
            <Clock class="w-4 h-4 text-base-content/50" />
          </div>
          <div>
            <div class="text-2xl font-black text-base-content/90">
              ~{formatGallons(budget.projected_month_end_gallons, $unitPreference)}
            </div>
            <p class="text-xs font-semibold mt-1 {projectedDelta > 0 ? 'text-error' : 'text-success'}">
              {projectedDelta > 0 ? '+' : ''}{formatGallons(projectedDelta, $unitPreference)} vs budget
            </p>
          </div>
          <div class="mt-4 pt-3 border-t border-base-content/5 flex items-center justify-between text-[11px] text-base-content/60">
            <span>Billing Window</span>
            <span class="font-mono text-base-content/75 text-[10px]">
              {budget.cycle_start_date && !isNaN(new Date(budget.cycle_start_date).getTime()) ? new Date(budget.cycle_start_date).toLocaleDateString(undefined, {month:'short', day:'numeric'}) : 'Cycle Start'} – {budget.cycle_end_date && !isNaN(new Date(budget.cycle_end_date).getTime()) ? new Date(budget.cycle_end_date).toLocaleDateString(undefined, {month:'short', day:'numeric'}) : 'Cycle End'}
            </span>
          </div>
        </div>

      </div>

      <!-- Personalized Conservation Guidance Banner -->
      {#if statusBadge}
        <div class="mt-4 p-4 rounded-2xl border flex items-start gap-3 transition-all
          {budget.pacing_status === 'on_track' ? 'bg-success/5 border-success/20 text-success' : 
           budget.pacing_status === 'at_risk' ? 'bg-warning/5 border-warning/20 text-warning' : 
           'bg-error/5 border-error/20 text-error'}">
          <statusBadge.icon class="w-5 h-5 shrink-0 mt-0.5" />
          <div class="text-xs space-y-0.5">
            <strong class="font-black text-sm block">
              {budget.pacing_status === 'on_track' ? 'Conservation Pace: Optimal' : 
               budget.pacing_status === 'at_risk' ? 'Pacing Notice: High Consumption Trend' : 
               'Budget Alert: Over Target Allowance'}
            </strong>
            <p class="text-base-content/75 leading-relaxed font-medium">
              {statusBadge.tip}
            </p>
          </div>
        </div>
      {/if}

    <!-- Tab 2: Building Peers & Quartiles -->
    {:else if activeTab === 'peers'}
      <div class="space-y-4">
        <!-- Quartile Standings Hero Card -->
        <div class="p-5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex items-center gap-3.5">
            <div class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 {peer.quartile_badge === 'water_guardian' ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/25' : peer.quartile_badge === 'conservation_champion' ? 'bg-sky-500/15 text-sky-500 border border-sky-500/25' : 'bg-amber-500/15 text-amber-500 border border-amber-500/25'}">
              <Award class="w-6 h-6" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-black uppercase tracking-wider text-base-content/50">Quartile Standing</span>
                <span class="badge badge-sm font-black text-white {peer.quartile_badge === 'water_guardian' ? 'badge-success' : peer.quartile_badge === 'conservation_champion' ? 'badge-info' : 'badge-warning'}">
                  {peer.quartile_badge === 'water_guardian' ? 'Top 10% · Water Guardian' : peer.quartile_badge === 'conservation_champion' ? 'Top 25% · Conservation Champion' : 'Median · Mindful User'}
                </span>
              </div>
              <div class="text-lg font-black text-base-content/90 mt-0.5">
                {peer.percentile_rank}th Percentile Efficiency
              </div>
              <p class="text-xs text-base-content/60">
                More efficient than {peer.percentile_rank}% of comparable residential units in the building.
              </p>
            </div>
          </div>

          <!-- Quick Delta Pill -->
          <div class="px-4 py-2.5 rounded-xl bg-base-content/[0.04] border border-base-content/5 text-right">
            <span class="text-[10px] uppercase font-bold text-base-content/50 block">vs Similar 2BR Units</span>
            <span class="text-base font-black text-emerald-500 font-mono">
              -{Math.max(0, Math.round(((peer.similar_floor_plan_gallons - budget.projected_month_end_gallons) / peer.similar_floor_plan_gallons) * 100))}%
            </span>
          </div>
        </div>

        <!-- 3 Comparison Benchmarks Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="p-4 rounded-2xl bg-base-content/[0.02] border border-base-content/5 space-y-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-base-content/50 block">Top 10% Efficient Tier</span>
            <div class="text-lg font-extrabold text-emerald-500 font-mono">
              {formatGallons(peer.top_10_percent_gallons, $unitPreference)}
            </div>
            <span class="text-[10px] text-base-content/60">Target for maximum rebate</span>
          </div>

          <div class="p-4 rounded-2xl bg-base-content/[0.02] border border-base-content/5 space-y-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-base-content/50 block">Similar Floor Plan (2BR)</span>
            <div class="text-lg font-extrabold text-sky-500 font-mono">
              {formatGallons(peer.similar_floor_plan_gallons, $unitPreference)}
            </div>
            <span class="text-[10px] text-base-content/60">Building average for 2-Bedrooms</span>
          </div>

          <div class="p-4 rounded-2xl bg-base-content/[0.02] border border-base-content/5 space-y-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-base-content/50 block">Building Overall Average</span>
            <div class="text-lg font-extrabold text-base-content/80 font-mono">
              {formatGallons(peer.building_average_gallons, $unitPreference)}
            </div>
            <span class="text-[10px] text-base-content/60">Across all property units</span>
          </div>
        </div>

        <!-- GPCD (Gallons Per Capita Daily) Meter -->
        <div class="p-5 rounded-2xl bg-base-content/[0.03] border border-base-content/5 space-y-3">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <Users class="w-4 h-4 text-primary" />
              <span class="text-xs font-black uppercase tracking-wider text-base-content/80">
                Gallons Per Capita Daily (GPCD)
              </span>
            </div>
            <!-- Occupant adjuster -->
            <div class="flex items-center gap-2 text-xs">
              <span class="text-base-content/60 text-[11px]">Occupants:</span>
              <div class="join border border-base-content/10 rounded-lg">
                <button 
                  type="button"
                  class="join-item btn btn-xs btn-ghost px-2 font-bold"
                  onclick={() => customOccupants = Math.max(1, effectiveOccupants - 1)}
                >-</button>
                <span class="join-item px-2.5 py-0.5 text-xs font-bold font-mono bg-base-content/5 flex items-center">
                  {effectiveOccupants}
                </span>
                <button 
                  type="button"
                  class="join-item btn btn-xs btn-ghost px-2 font-bold"
                  onclick={() => customOccupants = Math.min(8, effectiveOccupants + 1)}
                >+</button>
              </div>
            </div>
          </div>

          <div class="flex items-baseline justify-between">
            <div>
              <span class="text-2xl font-black text-primary font-mono">{calculatedGPCD}</span>
              <span class="text-xs font-semibold text-base-content/60 lowercase"> gal / person / day</span>
            </div>
            <span class="text-xs font-semibold {calculatedGPCD <= 42 ? 'text-emerald-500' : 'text-amber-500'}">
              {calculatedGPCD <= 42 ? '✔ Meets EPA WaterSense Target (42 GPCD)' : '▲ California Standard (55 GPCD)'}
            </span>
          </div>

          <!-- Benchmarking progress bar -->
          <div class="space-y-1">
            <div class="w-full bg-base-content/10 h-2.5 rounded-full overflow-hidden">
              <div 
                class="h-full rounded-full transition-all duration-500 {calculatedGPCD <= 42 ? 'bg-emerald-500' : calculatedGPCD <= 55 ? 'bg-amber-500' : 'bg-rose-500'}"
                style="width: {Math.min((calculatedGPCD / 70) * 100, 100)}%"
              ></div>
            </div>
            <div class="flex justify-between text-[10px] text-base-content/40 font-mono pt-0.5">
              <span>0 GPCD</span>
              <span class="text-emerald-500 font-bold">42 GPCD (Target)</span>
              <span>55 GPCD (State Cap)</span>
              <span>70+ GPCD</span>
            </div>
          </div>
        </div>
      </div>

    <!-- Tab 3: Tiered Protection -->
    {:else if activeTab === 'tiers'}
      <div class="space-y-4">
        <div class="p-4 rounded-2xl bg-base-content/[0.03] border border-base-content/5">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <BarChart3 class="w-4 h-4 text-primary" />
              <span class="text-xs font-black uppercase tracking-wider text-base-content/80">
                Municipal Water Conservation Tiers
              </span>
            </div>
            <span class="badge badge-sm font-bold {peer.current_tier === 1 ? 'badge-success text-white' : peer.current_tier === 2 ? 'badge-warning text-white' : 'badge-error text-white'}">
              Current: Tier {peer.current_tier}
            </span>
          </div>

          <!-- 3 Tiers Visual Stack -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div class="p-3.5 rounded-xl border {peer.current_tier === 1 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-base-content/[0.02] border-base-content/5'}">
              <div class="flex items-center justify-between text-xs font-bold mb-1">
                <span class="text-emerald-500">Tier 1 · Baseline</span>
                <span class="badge badge-xs badge-success text-white font-bold">Standard Rate</span>
              </div>
              <div class="text-base font-black text-base-content/90 font-mono">0 – {formatGallons(peer.tier1_limit_gallons, $unitPreference)}</div>
              <p class="text-[10px] text-base-content/60 mt-1">Indoor essential consumption</p>
            </div>

            <div class="p-3.5 rounded-xl border {peer.current_tier === 2 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-base-content/[0.02] border-base-content/5'}">
              <div class="flex items-center justify-between text-xs font-bold mb-1">
                <span class="text-amber-500">Tier 2 · Moderate</span>
                <span class="badge badge-xs badge-warning text-white font-bold">Standard + 15%</span>
              </div>
              <div class="text-base font-black text-base-content/90 font-mono">{formatGallons(peer.tier1_limit_gallons, $unitPreference)} – {formatGallons(peer.tier2_limit_gallons, $unitPreference)}</div>
              <p class="text-[10px] text-base-content/60 mt-1">Efficiency buffer zone</p>
            </div>

            <div class="p-3.5 rounded-xl border {peer.current_tier === 3 ? 'bg-rose-500/10 border-rose-500/30' : 'bg-base-content/[0.02] border-base-content/5'}">
              <div class="flex items-center justify-between text-xs font-bold mb-1">
                <span class="text-rose-500">Tier 3 · Surcharge</span>
                <span class="badge badge-xs badge-error text-white font-bold">+35% Drought Fee</span>
              </div>
              <div class="text-base font-black text-base-content/90 font-mono">&gt; {formatGallons(peer.tier2_limit_gallons, $unitPreference)}</div>
              <p class="text-[10px] text-base-content/60 mt-1">Municipal excessive penalty</p>
            </div>
          </div>

          <div class="mt-4 pt-3 border-t border-base-content/5 flex items-center justify-between text-xs text-base-content/70">
            <span>Buffer remaining before Tier 3 surcharge:</span>
            <span class="font-mono font-bold text-base-content/90">
              {formatGallons(Math.max(0, peer.tier2_limit_gallons - budget.mtd_consumption_gallons), $unitPreference)}
            </span>
          </div>
        </div>
      </div>

    <!-- Tab 4: Quiet-Flow Health -->
    {:else if activeTab === 'quiet_flow'}
      <div class="space-y-4">
        <div class="p-5 bg-base-content/[0.03] rounded-2xl border border-base-content/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex items-center gap-3.5">
            <div class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 {peer.quiet_flow_cleared ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/25' : 'bg-rose-500/15 text-rose-500 border border-rose-500/25'}">
              <ShieldCheck class="w-6 h-6" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-black uppercase tracking-wider text-base-content/50">Overnight Flow Verification</span>
                <span class="badge badge-sm font-black text-white {peer.quiet_flow_cleared ? 'badge-success' : 'badge-error'}">
                  {peer.quiet_flow_cleared ? 'VERIFIED LEAK-FREE' : 'FLOW DETECTED'}
                </span>
              </div>
              <div class="text-lg font-black text-base-content/90 mt-0.5">
                {peer.quiet_flow_streak_days}-Day Zero-Flow Streak
              </div>
              <p class="text-xs text-base-content/60">
                Last checked: {peer.last_quiet_flow_timestamp} · Ultrasonic submeter confirmed 0.00 LPH baseline.
              </p>
            </div>
          </div>

          <div class="text-right">
            <span class="badge badge-outline badge-sm text-[10px] font-mono">0.05 LPH Resolution</span>
          </div>
        </div>

        <div class="p-4 rounded-2xl bg-base-content/[0.02] border border-base-content/5 flex items-center justify-between text-xs">
          <div class="space-y-0.5">
            <strong class="font-bold text-base-content/90 block">Plumbing Integrity Check</strong>
            <p class="text-base-content/60 text-[11px]">Toilet flappers, faucet cartridges, and shower valves verified silent.</p>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span class="font-bold text-emerald-500 font-mono text-xs">100% HEALTHY</span>
          </div>
        </div>
      </div>
    {/if}
  {/if}
</GlassCard>

<!-- Modal: Adjust Monthly Target Goal -->
{#if showEditModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
    <div class="bg-base-100 border border-base-content/10 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <div class="p-2 rounded-xl bg-primary/10 text-primary">
            <Target class="w-5 h-5" />
          </div>
          <div>
            <h3 class="text-lg font-black text-base-content/90">Set Monthly Water Goal</h3>
            <p class="text-xs text-base-content/60">Customize conservation pacing for {unitName}</p>
          </div>
        </div>
        <button 
          class="btn btn-sm btn-ghost btn-circle"
          onclick={() => showEditModal = false}
          aria-label="Close"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      {#if saveError}
        <div class="alert alert-error text-xs rounded-xl">
          <AlertOctagon class="w-4 h-4" />
          <span>{saveError}</span>
        </div>
      {/if}

      <form onsubmit={handleSaveBudget} class="space-y-4">
        <div class="space-y-1.5">
          <label for="monthly-target-input" class="text-xs font-bold uppercase tracking-wider text-base-content/70">
            Monthly Target Allowance ({$unitPreference === 'metric' ? 'Gallons, will display in Liters' : 'Gallons'})
          </label>
          <div class="relative">
            <input 
              id="monthly-target-input"
              type="number" 
              bind:value={editTarget}
              min="500" 
              max="50000" 
              step="100"
              required
              class="input input-bordered w-full rounded-xl text-base font-bold pr-16" 
            />
            <span class="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-base-content/50 uppercase">
              gal
            </span>
          </div>
          <span class="text-[11px] text-base-content/50 block">
            Equivalent: {formatGallons(editTarget, $unitPreference)}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1.5">
            <label for="cycle-day-input" class="text-xs font-bold uppercase tracking-wider text-base-content/70">
              Cycle Start Day
            </label>
            <input 
              id="cycle-day-input"
              type="number" 
              bind:value={editCycleDay}
              min="1" 
              max="28" 
              required
              class="input input-bordered w-full rounded-xl text-sm" 
            />
          </div>

          <div class="space-y-1.5">
            <label for="alert-threshold-input" class="text-xs font-bold uppercase tracking-wider text-base-content/70">
              Alert Trigger (%)
            </label>
            <input 
              id="alert-threshold-input"
              type="number" 
              bind:value={editThreshold}
              min="50" 
              max="100" 
              required
              class="input input-bordered w-full rounded-xl text-sm" 
            />
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 pt-4 border-t border-base-content/10">
          <button 
            type="button" 
            class="btn btn-sm btn-ghost rounded-xl"
            onclick={() => showEditModal = false}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            class="btn btn-sm btn-primary rounded-xl font-bold gap-1.5"
            disabled={saving}
          >
            <Save class="w-3.5 h-3.5 {saving ? 'animate-spin' : ''}" />
            {saving ? 'Saving...' : 'Save Goal'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
